import { useEffect, useState } from "react";
import { fetchDevSuggestions } from "../lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "./ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { BookOpen, Briefcase, Award, Users, Inbox, Send, Clock, CheckCircle2, XCircle, Repeat, GraduationCap, ExternalLink, MessageSquare } from "lucide-react";

const TYPE_META = {
  course: { label: "Eğitim", icon: BookOpen, color: "sky" },
  project: { label: "Proje", icon: Briefcase, color: "amber" },
  certification: { label: "Sertifika", icon: Award, color: "emerald" },
  coaching: { label: "Koçluk", icon: Users, color: "rose" },
  book: { label: "Kitap", icon: BookOpen, color: "violet" },
};

const STATUS_BADGE = {
  pending: { label: "Beklemede", classes: "bg-amber-50 text-amber-800 border-amber-200" },
  accepted: { label: "Kabul edildi", classes: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  declined: { label: "Reddedildi", classes: "bg-rose-50 text-rose-800 border-rose-200" },
  counter: { label: "Karşı öneri yapıldı", classes: "bg-violet-50 text-violet-800 border-violet-200" },
  completed: { label: "Tamamlandı", classes: "bg-sky-50 text-sky-800 border-sky-200" },
};

function StatBox({ label, value, accent, Icon }) {
  const colors = {
    slate: "border-slate-200 text-slate-800 bg-slate-50",
    amber: "border-amber-300 text-amber-800 bg-amber-50",
    emerald: "border-emerald-300 text-emerald-800 bg-emerald-50",
    rose: "border-rose-300 text-rose-800 bg-rose-50",
    sky: "border-sky-300 text-sky-800 bg-sky-50",
    violet: "border-violet-300 text-violet-800 bg-violet-50",
  }[accent];
  return (
    <div className={`rounded-lg border ${colors} px-3 py-2.5`}>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[0.6rem] uppercase tracking-wider opacity-80">{label}</span>
        <Icon className="w-3.5 h-3.5 opacity-70" />
      </div>
      <div className="font-mono text-xl font-semibold">{value}</div>
    </div>
  );
}

function SuggestionOutRow({ item }) {
  const tm = TYPE_META[item.resource_type] || TYPE_META.course;
  const Icon = tm.icon;
  const sb = STATUS_BADGE[item.status] || STATUS_BADGE.pending;
  return (
    <div className="card-surface p-4" data-testid={`suggestion-out-${item.id}`}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 bg-${tm.color}-50 border border-${tm.color}-200`}>
          <Icon className={`w-4 h-4 text-${tm.color}-700`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[0.65rem] uppercase tracking-wider text-slate-500 mb-0.5">{tm.label} · {item.skill_name}</div>
          <div className="text-sm font-semibold text-slate-900 leading-snug">{item.title}</div>
          <div className="text-xs text-slate-500 flex items-center gap-3 mt-1 flex-wrap">
            {item.provider && <span>{item.provider}</span>}
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {item.duration_weeks} hafta</span>
            {item.url && (
              <a href={item.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-sky-700 hover:underline">
                <ExternalLink className="w-3 h-3" /> Link
              </a>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3 min-w-[200px]">
          <Avatar className="w-9 h-9">
            <AvatarImage src={item.employee_avatar} />
            <AvatarFallback>{item.employee_name?.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-xs font-medium truncate">{item.employee_name}</div>
            <div className="text-[0.65rem] text-slate-500 truncate">{item.employee_title}</div>
          </div>
        </div>
        <div className="text-right space-y-1.5">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.7rem] font-medium border ${sb.classes}`}>
            {sb.label}
          </span>
          <div className="text-[0.65rem] text-slate-400">
            {new Date(item.submitted_at).toLocaleDateString("tr-TR", { day: "numeric", month: "short" })}
          </div>
        </div>
      </div>

      {(item.response_message || item.counter_proposal) && (
        <div className="mt-3 pt-3 border-t border-slate-100 space-y-2">
          {item.response_message && (
            <div className="text-xs text-slate-700 flex items-start gap-2">
              <MessageSquare className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
              <span><span className="text-slate-500">{item.employee_name.split(" ")[0]}:</span> {item.response_message}</span>
            </div>
          )}
          {item.counter_proposal && (
            <div className="rounded-lg bg-violet-50 border border-violet-200 p-2.5">
              <div className="text-[0.65rem] uppercase tracking-wider text-violet-700 mb-1 flex items-center gap-1">
                <Repeat className="w-3 h-3" /> Karşı önerisi
              </div>
              <div className="text-sm text-violet-900 font-medium">{item.counter_proposal.title}</div>
              {item.counter_proposal.provider && <div className="text-xs text-violet-700">{item.counter_proposal.provider}</div>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SuggestionsOutboxPanel({ suggesterId }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    if (!suggesterId) return;
    setLoading(true);
    try {
      const d = await fetchDevSuggestions({ suggested_by_id: suggesterId });
      setItems(d.items || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggesterId]);

  const counts = items.reduce(
    (acc, it) => {
      acc[it.status] = (acc[it.status] || 0) + 1;
      acc.total += 1;
      return acc;
    },
    { pending: 0, accepted: 0, declined: 0, counter: 0, completed: 0, total: 0 },
  );

  const filtered = filter === "all" ? items : items.filter((i) => i.status === filter);

  return (
    <div className="space-y-5" data-testid="suggestions-outbox-panel">
      {/* Hero */}
      <div className="card-surface p-6 bg-gradient-to-br from-amber-50 via-white to-white border-amber-200">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-amber-500/15 border border-amber-300 flex items-center justify-center flex-shrink-0">
            <Send className="w-5 h-5 text-amber-700" />
          </div>
          <div className="flex-1">
            <div className="label-eyebrow text-amber-700 mb-1">Önerdiğim Gelişim Adımları</div>
            <h3 className="font-display text-2xl font-semibold tracking-tight">
              {counts.pending > 0 ? (
                <><span className="text-amber-700">{counts.pending}</span> öneri yanıt bekliyor</>
              ) : counts.total > 0 ? (
                `${counts.total} gelişim önerisi gönderdin`
              ) : (
                "Henüz gelişim önerisi göndermedin"
              )}
            </h3>
            <p className="text-sm text-slate-600 mt-1.5">
              Çalışanlara önerdiğin tüm gelişim adımları (eğitim, proje, sertifika, koçluk, kitap) ve yanıtları.
              Karşı öneri yapanları gözden geçirebilir, kabul edilenlerin ilerlemesini takip edebilirsin.
            </p>
          </div>
        </div>
        <div className="mt-5 grid grid-cols-3 md:grid-cols-6 gap-3">
          <StatBox label="Toplam" value={counts.total} accent="slate" Icon={Send} />
          <StatBox label="Bekleyen" value={counts.pending} accent="amber" Icon={Clock} />
          <StatBox label="Kabul" value={counts.accepted} accent="emerald" Icon={CheckCircle2} />
          <StatBox label="Karşı öneri" value={counts.counter} accent="violet" Icon={Repeat} />
          <StatBox label="Red" value={counts.declined} accent="rose" Icon={XCircle} />
          <StatBox label="Tamam" value={counts.completed} accent="sky" Icon={GraduationCap} />
        </div>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="bg-white border border-slate-200 mb-4 p-1.5 h-auto flex-wrap">
          <TabsTrigger value="all" data-testid="outbox-f-all" className="text-sm px-4 py-1.5 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-900 data-[state=active]:shadow-sm">Tümü ({counts.total})</TabsTrigger>
          <TabsTrigger value="pending" data-testid="outbox-f-pending" className="text-sm px-4 py-1.5 data-[state=active]:bg-amber-50 data-[state=active]:text-amber-900 data-[state=active]:shadow-sm">Bekleyen ({counts.pending})</TabsTrigger>
          <TabsTrigger value="accepted" data-testid="outbox-f-accepted" className="text-sm px-4 py-1.5 data-[state=active]:bg-emerald-50 data-[state=active]:text-emerald-900 data-[state=active]:shadow-sm">Kabul ({counts.accepted})</TabsTrigger>
          <TabsTrigger value="counter" data-testid="outbox-f-counter" className="text-sm px-4 py-1.5 data-[state=active]:bg-violet-50 data-[state=active]:text-violet-900 data-[state=active]:shadow-sm">Karşı öneri ({counts.counter})</TabsTrigger>
          <TabsTrigger value="declined" data-testid="outbox-f-declined" className="text-sm px-4 py-1.5 data-[state=active]:bg-rose-50 data-[state=active]:text-rose-900 data-[state=active]:shadow-sm">Red ({counts.declined})</TabsTrigger>
          <TabsTrigger value="completed" data-testid="outbox-f-completed" className="text-sm px-4 py-1.5 data-[state=active]:bg-sky-50 data-[state=active]:text-sky-900 data-[state=active]:shadow-sm">Tamam ({counts.completed})</TabsTrigger>
        </TabsList>
        <TabsContent value={filter} className="space-y-3">
          {loading && (
            <div className="card-surface p-8">
              <div className="h-4 bg-slate-200/60 rounded animate-pulse w-1/3 mb-2" />
              <div className="h-3 bg-slate-100 rounded animate-pulse w-2/3" />
            </div>
          )}
          {!loading && filtered.length === 0 && (
            <div className="card-surface p-12 text-center" data-testid="outbox-empty">
              <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
              <h3 className="font-display text-lg font-semibold text-slate-900">Bu kategoride öneri yok</h3>
              <p className="text-sm text-slate-500 mt-1.5 max-w-md mx-auto">
                Çalışanlara gelişim önerisi gönderdiğinde durumları burada görünecek.
              </p>
            </div>
          )}
          {!loading && filtered.map((it) => <SuggestionOutRow key={it.id} item={it} />)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
